/**
 * Agentic Operations nav — this surface's OWN sidebar (ADR-0001 §2.2: the
 * agentic/ deployable has isolated nav/entitlements; it never touches
 * frontend/'s LeftRail). Future sections land here as their pages ship.
 */
import {
  AlertTriangle,
  Bot,
  BookOpen,
  Gauge,
  GitPullRequest,
  Inbox,
  LayoutDashboard,
  LogOut,
  ScrollText,
  ShieldAlert,
  Users,
} from 'lucide-react';
import { NavLink } from 'react-router';
import { Icon, Text, cn } from '@smms/design-system';
import { useAuth } from '../auth/context';

const NAV_ITEMS = [
  { to: '/', label: 'Overview', icon: LayoutDashboard, end: true },
  { to: '/decisions', label: 'Decision Inbox', icon: Inbox, end: false },
  { to: '/exceptions', label: 'Exceptions', icon: AlertTriangle, end: false },
  { to: '/agents', label: 'Agents & Trust', icon: Users, end: false },
  { to: '/policies', label: 'Policies & Safety', icon: ShieldAlert, end: false },
  { to: '/runs', label: 'Runs & Audit', icon: ScrollText, end: false },
  { to: '/knowledge', label: 'Knowledge Health', icon: BookOpen, end: false },
  { to: '/approvals', label: 'Config Approvals', icon: GitPullRequest, end: false },
  { to: '/usage', label: 'Usage & Budgets', icon: Gauge, end: false },
];

// Shipped backend surfaces without pages yet — listed so the information
// architecture is visible, rendered inert (no dead links). Empty now that the
// read-only IA is built out; Settings lands here next.
const UPCOMING: string[] = [];

export default function AgenticNav() {
  const { user, logout } = useAuth();

  return (
    <nav
      aria-label="Agentic Operations"
      className="flex h-full w-60 shrink-0 flex-col border-r border-border bg-surface"
    >
      <div className="flex items-center gap-2 px-4 py-5">
        <Icon as={Bot} size="md" className="text-primary" />
        <div>
          <Text className="font-semibold leading-tight">
            SMMS Agentic
          </Text>
          <Text variant="caption" tone="muted" className="leading-tight">
            Operations
          </Text>
        </div>
      </div>

      <ul className="flex-1 space-y-1 px-2">
        {NAV_ITEMS.map(item => (
          <li key={item.to}>
            <NavLink
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted hover:bg-elevated hover:text-text',
                )
              }
            >
              <Icon as={item.icon} size="sm" />
              {item.label}
            </NavLink>
          </li>
        ))}
        {UPCOMING.length > 0 && (
          <li aria-hidden="true" className="px-3 pb-1 pt-4">
            <Text variant="caption" tone="subtle" className="uppercase tracking-wide">
              Coming soon
            </Text>
          </li>
        )}
        {UPCOMING.map(label => (
          <li key={label}>
            <span className="flex cursor-default items-center gap-2 rounded-md px-3 py-2 text-sm text-disabled">
              {label}
            </span>
          </li>
        ))}
      </ul>

      <div className="border-t border-border px-4 py-3">
        {user && (
          <Text variant="caption" tone="muted" className="mb-2 block truncate">
            {user.email}
          </Text>
        )}
        <button
          type="button"
          onClick={() => void logout()}
          className="flex items-center gap-2 text-sm text-muted transition-colors hover:text-text"
        >
          <Icon as={LogOut} size="sm" />
          Sign out
        </button>
      </div>
    </nav>
  );
}
